import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useUnsavedChangesWarning from "../../hooks/useUnsavedChangesWarning";
import UnsavedChangesModal from "../common/modal/UnsavedChangesModal";
import { Tabs, Tab, Accordion, AccordionItem } from "@heroui/react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import usePrefetchProducts from "../../hooks/usePrefetchProducts";
import usePrefetchItems from "../../hooks/usePrefetchItems";
import useCustomerInstructions from "@/hooks/useCustomerInstructions";
import usePastPurchases from "@/hooks/usePastPurchases";
import { SelectProvider } from "../../context/SelectContext";
import { useUseCases } from "@/context/UseCasesProvider";
import { v4 as uuidv4 } from "uuid";
import useUser from "@/hooks/useUser";

import EmailContentAdvanced from "./EmailContentAdvanced";
import BasicExampleWithImage from "./Shapes.tsx";
import { coordinatesData } from "./coordinatesData";
import UserActionsFeed from "./UserActionsFeed.jsx";
import InternalNote from "./InternalNote";
import ActionsToolbar from "./ActionsToolbar";
import useUserProfile from "../../hooks/useUserProfile";
import useLocalStorageList from "../../hooks/useLocalStorageList";
import useConfigFieldsValues from "../../hooks/useConfigFieldsValues";
import CustomerInstructionModal from "components/common/modal/CustomerInstructionModal";
import Intents from "./Intents";
import { useWebSocketContext } from "@/context/WebSocketContext";
import useAuth from "@/hooks/useAuth";

import Products from "./Products";
import StatusWarning from "./StatusWarning";
import CustomerInfoTab from "./CustomerInfoTab";
import PaginationComponent from "./PaginationComponent";
import ThirdPartyReference from "./ThirdPartyReference";
import DebugPanel from "./DebugPanel.tsx";

import { IoWarningOutline } from "react-icons/io5";
import BoxesLoader from "components/common/layout/loaderBox";
import { useTemplateLoader } from "../../hooks/useTemplateLoader";
import InfosCustomersDefault from "../specifics/InfosCustomers_Default";

export default function Order() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { isDataReadOnlyOperators, isAdminVistaflow } = useUserProfile();
  const [selected, setSelected] = React.useState("infos");
  const [dataForm, setDataForm] = useState();
  const [shouldSendPutRequest, setShouldSendPutRequest] = useState(false);
  const [isCustomerSelected, setIsCustomerSelected] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isErrorsInit, setIsErrorsInit] = useState(false);
  const [internalNoteInit, setInternalNoteInit] = useState(false);
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const queryClient = useQueryClient();
  const { removeItem } = useLocalStorageList("unreadOrders", `${params.orderId}`);
  const { extractionFields, checksOrderMetadata } = useConfigFieldsValues();
  const settingsInterface = useUseCases();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [isScrollSynced, setIsScrollSynced] = useState(true);

  const { data: userData } = useUser();
  const { auth } = useAuth();

  const { sendMessage, subscribeMessage, isConnected } = useWebSocketContext();
  const companyId = auth?.clientId;
  const orderId = params.orderId;
  const userId = userData?.user_id;
  const sessionIdRef = useRef(uuidv4());
  const sessionId = sessionIdRef.current;

  const hasSubscribed = useRef(false);

  useEffect(() => {
    if (!orderId || !companyId || !userId) return;

    const handleBeforeUnload = () => {
      sendMessage({
        action: "unsubscribe",
        company_id: companyId,
        order_id: orderId,
        user_id: userId,
        session_id: sessionId,
      });
    };

    if (isConnected && !hasSubscribed.current) {
      subscribeMessage({
        action: "subscribe",
        company_id: companyId,
        order_id: orderId,
        user_id: userId,
        session_id: sessionId,
      });
      hasSubscribed.current = true;
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      if (isConnected) {
        sendMessage({
          action: "unsubscribe",
          company_id: companyId,
          order_id: orderId,
          user_id: userId,
        });
        hasSubscribed.current = false;
      }
    };
  }, [isConnected, companyId, orderId, userId, sendMessage, subscribeMessage]);

  const { initAllProducts } = usePrefetchProducts("products");
  const prevProductIdsRef = React.useRef(null);
  useEffect(() => {
    if (dataForm && dataForm.order && dataForm.order.products) {
      const productIds = dataForm.order.products
        .map((product) => product.product_id.value)
        .filter((id) => id && id !== "");
      const productIdsKey = productIds.join(",");
      if (productIds.length > 0 && productIdsKey !== prevProductIdsRef.current) {
        prevProductIdsRef.current = productIdsKey;
        initAllProducts(productIds);
      }
    }
  }, [dataForm, initAllProducts]);

  const { initAllItems: initAllCustomers } = usePrefetchItems("customers", "customer");

  const { data: customerGroupData } = useQuery({
    queryKey: ["listCustomer"],
    enabled: false,
  });

  useEffect(() => {
      if (!dataForm?.order?.metadata) return;
      const customerId = dataForm.order.metadata.customer_id?.value;
      
      const addressIds = [];
      
      if (checksOrderMetadata?.delivery_address_matching?.value && checksOrderMetadata?.delivery_address_matching?.onlyInDeliveryPerCustomer === false) {
        if (dataForm.order?.metadata.delivery_address?.value) {
          addressIds.push(dataForm.order.metadata.delivery_address.value);
        }
      }
      
      if (checksOrderMetadata?.billing_address_matching?.value && checksOrderMetadata?.billing_address_matching?.onlyInDeliveryPerCustomer === false) {
        if (dataForm.order?.metadata?.billing_address?.value) {
          addressIds.push(dataForm.order.metadata.billing_address.value);
        }
      }
      
      if (checksOrderMetadata?.sender_address_matching?.value && checksOrderMetadata?.sender_address_matching?.onlyInDeliveryPerCustomer === false) {
        if (dataForm.order?.metadata?.sender_postal_address?.value) {
          addressIds.push(dataForm.order.metadata.sender_postal_address.value);
        }
      }
      
      const idsToLoad = [customerId, ...addressIds].filter(Boolean);
      
      if (idsToLoad.length > 0) {
        initAllCustomers(idsToLoad.join(','));
      } else {
        queryClient.removeQueries("listCustomer");
      }
  }, [dataForm, initAllCustomers, queryClient, checksOrderMetadata]);

  const methods = useForm({
    mode: "onBlur",
    defaultValues: dataForm || {},
  });
  const { watch, formState: { isDirty } } = methods;
  const [customerIDSelect, setCustomerIDSelect] = useState();
  const [customerGroupIdSelect, setCustomerGroupIdSelect] = useState();
  const [shouldReloadData, setShouldReloadData] = useState(false);

  const selectedCustomer = watch("selectcustomer");
  useEffect(() => {
    const fetchCustomerGroupData = async () => {
      try {
        if (customerIDExists) {
          const response = await axiosPrivate.get(
            `/customers?customer_id=${dataForm.order.metadata.customer_id.value}`
          );
          const customerGroupIdList = response.data.data[0]?.customer_group_id_list;
          setCustomerGroupIdSelect(
            customerGroupIdList && customerGroupIdList.length > 0 ? customerGroupIdList[0] : null
          );
        } else {
          setCustomerGroupIdSelect(null);
        }
      } catch (error) {
        console.error("Error fetching customer group data:", error);
        setCustomerGroupIdSelect(null);
      }
    };
    const customerIDExists = !!dataForm?.order?.metadata?.customer_id?.value &&
      dataForm?.order?.metadata?.customer_id?.value != "None";
    const valueConfidenceIsHighEnough =
      dataForm?.order?.metadata?.customer_id?.valueConfidence >= 75;
    const isValidSelectedCustomer = !!selectedCustomer && selectedCustomer != "None";
    setIsCustomerSelected((customerIDExists && valueConfidenceIsHighEnough) || isValidSelectedCustomer);

    let customerIDToSelect = null;
    if (isValidSelectedCustomer) {
      customerIDToSelect = selectedCustomer?.value ?? selectedCustomer;
    } else if (customerIDExists) {
      customerIDToSelect = dataForm?.order?.metadata?.customer_id?.value;
    }
    setCustomerIDSelect(customerIDToSelect);
    if (customerIDExists) {
      fetchCustomerGroupData();
    } else {
      setCustomerGroupIdSelect(null);
    }
  }, [dataForm, selectedCustomer, customerGroupData, axiosPrivate]);

  useEffect(() => {
    const customerError = errors.find((error) => error.customer_idCorrected === true);
    if (customerError) {
      setIsCustomerSelected(true);
    }
  }, [errors]);

  const [isInitialized, setIsInitialized] = useState(false);
  const [hasFormBeenTouched, setHasFormBeenTouched] = useState(false);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      console.log(`Field ${name} changed:`, value[name]);
      // for modal alert in case of update
      if (isInitialized && name) {
        setHasFormBeenTouched(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, isInitialized]);

  const handleRemoveItem = useCallback(() => {
    removeItem();
  }, [removeItem]);

  useEffect(() => {
    if (dataForm && !isInitialized) {
      const visibleFields = extractionFields.filter((field) => field.isVisible);

      const formValues = visibleFields.reduce((acc, field) => {
        acc[field.key] = dataForm.order?.metadata?.[field.key]?.value || null;
        return acc;
      }, {});

      formValues.customer_name = dataForm.order?.metadata?.customer_name?.value || null;
      formValues.internal_note = dataForm.process?.internal_note || null;

      methods.reset(formValues);

      if (dataForm.process?.internal_note) {
        setInternalNoteInit(true);
      }
      setIsInitialized(true);
      handleRemoveItem(); // unread
    }
  }, [dataForm, methods, isInitialized, handleRemoveItem, extractionFields]);

  // Get order ID
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const getCustomers = async () => {
      try {
        const response = await axiosPrivate.get(`/orders/${params.orderId}`, {
          signal: controller.signal,
        });
        if (
          (response.data.process && (response.data.process.status === "failed" || response.data.process.status === "active")) ||
          !response.data.order.metadata
        ) {
          navigate(`/orderserror/${params.orderId}`);
          return;
        }
        if (isMounted) {
          setDataForm(response.data);
          setInitialDataLoading(false);
        }
      } catch (err) {
        console.log("Catch error axios : " + JSON.stringify(err, null, 2));
      }
    };

    getCustomers();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [axiosPrivate, params.orderId, navigate]);

  const updateDataForm = (formData) => {
    const productsData = Object.keys(formData)
      .filter((key) => key.startsWith("products-"))
      .reduce((acc, key) => {
        const productIndex = key.split("-")[1];
        const productData = formData[key];
        const productInfo = typeof productData === "object" ? productData : { value: productData };

        // Delete ID at the beginning of label
        if (productInfo?.label) {
          const parts = productInfo.label.split(" - ");
          parts.shift(); // Supprimer l'ID
          productInfo.label = parts.join(" - "); // Glue the rest
        }

        acc[productIndex] = productInfo;
        return acc;
      }, {});

    //console.log(`productsData :`, productsData);

    // Processing modified product quantities
    const productQuantitiesData = Object.keys(formData)
      .filter((key) => key.startsWith("productQuantity-"))
      .reduce((acc, key) => {
        const productIndex = key.split("-")[1];
        const productQuantity = formData[key];

        acc[productIndex] = { quantity: productQuantity };
        return acc;
      }, {});

    //console.log(productQuantitiesData);

    const productsVariantsData = Object.keys(formData)
      .filter((key) => key.startsWith("productVariants-"))
      .reduce((acc, key) => {
        const productIndex = key.split("-")[1];
        const productVariant = formData[key];
        const productInfo =
          typeof productVariant === "object" ? productVariant : { value: productVariant };

        acc[productIndex] = productInfo;
        return acc;
      }, {});

    const productPackingCustomerData = Object.keys(formData)
      .filter((key) => key.startsWith("productPackingCustomer-"))
      .reduce((acc, key) => {
        const productIndex = key.split("-")[1];
        const productPackingCustomer = formData[key];

        acc[productIndex] = { packingCustomer: productPackingCustomer };
        return acc;
      }, {});

    const updatedProducts = dataForm.order.products.map((product, index) => {
      const productIndex = index;
      const updatedProductData = productsData[productIndex];
      const updatedProductQuantity = productQuantitiesData[productIndex];
      const updatedProductVariant = productsVariantsData[productIndex];
      const updatedProductPackingCustomer = productPackingCustomerData[productIndex];

      let updatedProduct = { ...product };
      // Updating product data
      if (updatedProductData && updatedProductData.product_id) {
        updatedProduct = {
          ...updatedProduct,
          product_id: {
            ...updatedProduct.product_id,
            value: updatedProductData.product_id,
            valueConfidence: 100,
            valueConfidenceSource: updatedProduct.product_id.valueConfidenceSource,
          },
          product_name: {
            ...updatedProduct.product_name,
            value: updatedProductData.product_name,
            valueConfidence: 100,
            valueConfidenceSource: updatedProduct.product_id.valueConfidenceSource,
          },
        };
      }

      // Update product_packing_customer from products-${index}.product_packing_customer
      if (updatedProductData?.product_packing_customer) {
        updatedProduct = {
          ...updatedProduct,
          product_packing_customer: {
            ...updatedProduct.product_packing_customer,
            value: updatedProductData.product_packing_customer,
            valueConfidence: 100,
            valueConfidenceSource: "hitl",
          },
        };
      }

      // Update product quantity
      if (updatedProductQuantity && updatedProductQuantity.quantity) {
        updatedProduct = {
          ...updatedProduct,
          product_quantity: {
            ...updatedProduct.product_quantity,
            value: updatedProductQuantity.quantity,
            valueConfidence: 100,
            valueConfidenceSource: updatedProduct.product_quantity?.valueConfidenceSource,
          },
        };
      }

      // Update product variant
      if (updatedProductVariant && updatedProductVariant.value) {
        const variantChanged =
          updatedProductVariant.value !== updatedProduct.product_variant_id?.value;
        updatedProduct = {
          ...updatedProduct,
          product_variant_id: {
            ...updatedProduct.product_variant_id,
            value: updatedProductVariant.value,
            valueConfidence: variantChanged ? 100 : updatedProduct.product_variant_id?.valueConfidence,
            valueConfidenceSource: variantChanged ? "hitl" : updatedProduct.product_variant_id?.valueConfidenceSource,
          },
          product_variant_name: {
            ...updatedProduct.product_variant_name,
            value: updatedProductVariant.label,
            valueConfidence: variantChanged ? 100 : updatedProduct.product_variant_name?.valueConfidence,
            valueConfidenceSource: variantChanged ? "hitl" : updatedProduct.product_variant_name?.valueConfidenceSource,
          },
        };
      }

      // Update product packing customer
      if (updatedProductPackingCustomer?.packingCustomer) {
        updatedProduct = {
          ...updatedProduct,
          product_packing_customer: {
            ...updatedProduct.product_packing_customer,
            value: updatedProductPackingCustomer.packingCustomer,
            valueConfidence: 100,
            valueConfidenceSource: updatedProduct.product_packing_customer?.valueConfidenceSource || "hitl",
          },
        };
      }
      return updatedProduct;
    });

    setDataForm((prevData) => {
      const updatedMetadata = { ...prevData.order.metadata };

      Object.keys(formData).forEach((key) => {
        if (
          key.startsWith("products-") ||
          key.startsWith("productQuantity-") ||
          key.startsWith("productVariants-") ||
          key.startsWith("productPackingCustomer-") ||
          key === "internal_note"
        ) {
          return;
        }

        if (key === "selectcustomer") {
          const baseMetadata = {
            valueConfidence: 100,
            valueConfidenceSource: "hitl",
          };

          if (formData["customer_id_validated"]) {
            updatedMetadata.customer_id = {
              ...updatedMetadata.customer_id,
              ...baseMetadata,
            };
            updatedMetadata.customer_name = {
              ...updatedMetadata.customer_name,
              ...baseMetadata,
            };
          } else if (formData.selectcustomer?.label) {
            updatedMetadata.customer_id = {
              ...updatedMetadata.customer_id,
              value: formData.selectcustomer.value,
              ...baseMetadata,
            };
            updatedMetadata.customer_name = {
              ...updatedMetadata.customer_name,
              value: extractNameFromLabel(formData.selectcustomer.label),
              ...baseMetadata,
            };
          }
          return;
        }

        const isFieldValidated = formData[`${key}_validated`];

        if (prevData.order.metadata.hasOwnProperty(key)) {
          const newValue =
            formData[key] !== undefined && formData[key] !== null
              ? formData[key]
              : prevData.order.metadata[key]?.value || "";
          const hasValueChanged = newValue !== prevData.order.metadata[key]?.value;
          const shouldUpdateConfidence = isFieldValidated || hasValueChanged;
          updatedMetadata[key] = {
            ...updatedMetadata[key],
            value: newValue,
            valueConfidence: shouldUpdateConfidence ? 100 : updatedMetadata[key].valueConfidence,
            valueConfidenceSource: shouldUpdateConfidence
              ? "hitl"
              : updatedMetadata[key].valueConfidenceSource,
          };
        }

        const addressFields = ["delivery_address", "billing_address", "sender_postal_address"];

        if (addressFields.includes(key)) {
          const addressDetails = formData[`${key}_details`];
          if (addressDetails) {
            updatedMetadata[key] = {
              ...updatedMetadata[key],
              valueDetail: {
                ...addressDetails.valueDetail,
              },
            };
          } else if (formData[key]) {
            updatedMetadata[key] = {
              ...updatedMetadata[key],
              value: formData[key],
            };
          }
        }
      });

      const newData = {
        ...prevData,
        order: {
          ...prevData.order,
          products: updatedProducts,
          metadata: updatedMetadata,
        },
        process: {
          ...prevData.process,
          internal_note: formData.internal_note || prevData.process.internal_note,
        },
      };

      console.log(`newData :`, newData);
      setIsInitialized(false);
      methods.reset(newData);
      return newData;
    });
    setShouldSendPutRequest(true);
  };

  const extractNameFromLabel = (label) => {
    const addressStartIndex = label.indexOf(" - (");

    // If "- (" is found in the string, extract all that precedes it
    if (addressStartIndex !== -1) {
      const nameWithId = label.substring(0, addressStartIndex);

      // Split with "-" and remove ID
      const parts = nameWithId.split(" - ");
      if (parts.length > 1) {
        return parts[1];
      }
    }
    return label;
  };

  const handleProductDelete = (index) => {
    setDataForm((prevData) => {
      const updatedProducts = prevData?.order.products.filter((_, idx) => idx !== index);

      return {
        ...prevData,
        order: { ...prevData?.order, products: updatedProducts },
      };
    });

    deleteIndexError(index);
    updateIndices(index, "delete");
  };

  const handleAddProduct = (data, currentPage, valueConfidence) => {
    setDataForm((prevData) => {
      const newProduct = {
        product_id: {
          value: data.product.value,
          valueConfidence: valueConfidence,
          valueConfidenceSource: "manual",
        },
        product_name: {
          value: data.product.label,
          valueConfidence: valueConfidence,
          valueConfidenceSource: "manual",
        },
        product_quantity: {
          value: data.quantity,
          valueConfidence: valueConfidence,
          valueConfidenceSource: "manual",
        },
        product_variant_id: {
          value: data.variant.value,
          valueConfidence: 100,
          valueConfidenceSource: "manual",
        },
        product_variant_name: {
          value: data.variant.label,
          valueConfidence: 100,
          valueConfidenceSource: "manual",
        },
        product_pageNumber: { value: currentPage },
      };
      // Clone the current products array
      const updatedProducts = [...prevData.order.products];

      // Insert the new product at the specific index
      updatedProducts.splice(data.position, 0, newProduct);

      return {
        ...prevData,
        order: {
          ...prevData.order,
          products: updatedProducts,
        },
      };
    });
    setFieldChanged(true);
    addIndexError(data.position, currentPage, valueConfidence);
    updateIndices(data.position, "add");
  };

  const handleProductUpdate = useCallback((index, updates) => {
    setDataForm((prevData) => {
      const updatedProducts = [...prevData.order.products];
      if (updatedProducts[index]) {
        for (const key of Object.keys(updates)) {
          if (updatedProducts[index][key]) {
            updatedProducts[index][key] = {
              ...updatedProducts[index][key],
              ...updates[key],
            };
          }
        }
      }
      return {
        ...prevData,
        order: { ...prevData.order, products: updatedProducts },
      };
    });
  }, []);

  useEffect(() => {
    const sendPutRequest = async () => {
      if (shouldSendPutRequest && dataForm) {
        setIsSending(true);
        try {
          const response = await axiosPrivate.put("/orders/" + params.orderId, dataForm);
          setShouldSendPutRequest(false);
          setDataForm(response.data);
          setShouldReloadData(true);
          setSelectedIndices(new Set());
          setHasFormBeenTouched(false);
        } catch (err) {
          if (err.response && err.response.status >= 400 && err.response.status < 500) {
            globalThis.location.reload();
          } else {
            console.log("Erreur lors de la requête PUT: ", err);
          }
        } finally {
          setIsSending(false);
        }
      }
    };

    sendPutRequest();
  }, [dataForm, shouldSendPutRequest, axiosPrivate, params.orderId]);

  const onSubmit = async (formData) => {
    console.log("data sur la onsubmit : " + JSON.stringify(formData, null, 2));
    updateDataForm(formData);
  };

  const classNames = React.useMemo(
    () => ({
      tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
      cursor: "w-full bg-vistagreen",
      tab: "max-w-fit px-0 h-12 border-vistagreen",
      tabContent: "group-data-[selected=true]:text-vistagreen",
      panel: ["w-full bg-white rounded-lg px-0 pb-0"],
      label: [
        "text-[10px] uppercase text-vistagrey/60 z-0  group-data-[filled=true]:text-vistagrey/60",
      ],
      input: [],
      inputWrapper: [""],
      mainWrapper: [],
      base: ["pt-2 "],
    }),
    []
  );
  const itemClasses = React.useMemo(
    () => ({
      base: "mt-4 w-full px-0",
      title: "font-normal text-medium text-white font-bold",
      trigger: "px-2 bg-[#d79a5f] rounded-t-lg h-10 flex items-center",
      content: "text-small px-4",
      indicator: "text-white",
    }),
    []
  );

  const [page, setPage] = React.useState(1);
  const pages = dataForm?.process?.email?.images?.length;
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const [initialDataLoading, setInitialDataLoading] = useState(true);

  const [fieldChanged, setFieldChanged] = useState(false);

  useEffect(() => {
    const getIsMandatoryAndVisibleFromGeneralValue = (key) => {
      const field = extractionFields.find((item) => item.key === key);
      return field?.isMandatory === true && field?.isVisible === true;
    };

    const initializeTrackingIds = (formData) => {
      const isTemplate2 = settingsInterface?.settings?.display?.product_quantity?.template === 2;
      const trackingDetails =
        formData?.order?.products.map((product, index) => ({
          index,
          page: product.product_pageNumber.value,
          productIdCorrected: product.product_id.valueConfidence >= 75,
          quantityIdCorrected: product.product_quantity.valueConfidence >= 75,
          variantIdCorrected: product.product_variant_id.valueConfidence >= 75,
          packingCustomerIdCorrected: isTemplate2
            ? ((product.product_packing_customer?.valueConfidence ?? 0) >= 75)
            : true,
        })) || [];

      const metadata = formData?.order?.metadata || {};
      Object.keys(metadata).forEach((key) => {
        const field = metadata[key];
        const isMandatoryAndVisible = getIsMandatoryAndVisibleFromGeneralValue(key);

        if (
          (isMandatoryAndVisible &&
            field.valueConfidence !== undefined &&
            field.valueConfidence < 75) ||
          (isMandatoryAndVisible && !field.value)
        ) {
          const errorObject = {
            index: key,
          };
          errorObject[`${key}Corrected`] = false;
          trackingDetails.push(errorObject);
        }
      });

      console.log(`trackingDetails init = `, trackingDetails);
      if (trackingDetails.length > 0) {
        setErrors(trackingDetails);
        setIsErrorsInit(true);
      }
    };
    if (dataForm && !isInitialized) {
      initializeTrackingIds(dataForm);
    }
  }, [dataForm, isInitialized, extractionFields]);

  // Decrement error counter each time a field is modified
  useEffect(() => {
    if (fieldChanged) {
      setFieldChanged(false);
    }
  }, [fieldChanged]);

  const [hoveredShapeId, setHoveredShapeId] = useState({});

  const handleHoveredShapeId = (action, canvasId) => {
    setHoveredShapeId({ action, canvasId });
  };

  //event onHover shape
  const [hoveredShapeDraw, setHoveredShapeDraw] = useState({});

  const handleHoveredShapeDraw = (action, canvasId) => {
    setHoveredShapeDraw({ action, canvasId });
  };

  const [shapesPerPage, setShapesPerPage] = useState(null);
  useEffect(() => {
    if (dataForm && dataForm.order && dataForm.order.products) {
      const newShapes = coordinatesData(dataForm.order.products, {
        checkVariant: settingsInterface?.settings?.display?.product_display_column?.variant === true,
        checkPackingCustomer: settingsInterface?.settings?.display?.product_display_column?.packing_customer === true,
      });
      setShapesPerPage(newShapes);
    }
  }, [dataForm, settingsInterface]);

  const currentShapes = shapesPerPage ? shapesPerPage[page] : null;

  // Error management
  const handleTrackingError = (index, { productError, quantityError, variantError, packingCustomerError }) => {
    setErrors((prevErrors) => {
      const elementIndex = prevErrors.findIndex((error) => error.index === index);

      const getProductIdCorrected = () => {
        if (productError !== undefined) return productError;
        if (elementIndex === -1) return false;
        return prevErrors[elementIndex].productIdCorrected;
      };

      const getQuantityIdCorrected = () => {
        if (quantityError !== undefined) return quantityError;
        if (elementIndex === -1) return false;
        return prevErrors[elementIndex].quantityIdCorrected;
      };

      const getVariantIdCorrected = () => {
        if (variantError !== undefined) return variantError;
        if (elementIndex === -1) return false;
        return prevErrors[elementIndex].variantIdCorrected;
      };

      const getPackingCustomerIdCorrected = () => {
        if (packingCustomerError !== undefined) return packingCustomerError;
        if (elementIndex === -1) return true;
        return prevErrors[elementIndex].packingCustomerIdCorrected;
      };

      const newError = {
        index: index,
        page: prevErrors[elementIndex].page,
        productIdCorrected: getProductIdCorrected(),
        quantityIdCorrected: getQuantityIdCorrected(),
        variantIdCorrected: getVariantIdCorrected(),
        packingCustomerIdCorrected: getPackingCustomerIdCorrected(),
      };
      console.log(`newError`, newError);

      if (elementIndex === -1) {
        return [...prevErrors, newError];
      } else {
        // Copy of table to avoid direct transfer
        const updatedErrors = [...prevErrors];
        updatedErrors[elementIndex] = newError;
        return updatedErrors;
      }
    });
  };

  const deleteIndexError = (indexToDelete) => {
    setErrors((prevErrors) => {
      // Delete element at specified index
      const filteredErrors = prevErrors.filter((error) => error.index !== indexToDelete);

      // Adjust indexes for all elements whose index is greater than indexToDelete
      const adjustedErrors = filteredErrors.map((error) => {
        if (typeof error.index === "number" && error.index > indexToDelete) {
          return { ...error, index: error.index - 1 };
        } else {
          return error;
        }
      });
      return adjustedErrors;
    });
  };

  const addIndexError = (indexToAdd, page, valueConfidence) => {
    setErrors((prevErrors) => {
      const adjustedErrors = () => {
        let newErrors = [...prevErrors];
        const isTemplate2 = settingsInterface?.settings?.product_quantity?.template === 2;
        const newError = {
          index: parseInt(indexToAdd, 10),
          page: parseInt(page, 10),
          productIdCorrected: valueConfidence === 100,
          quantityIdCorrected: valueConfidence === 100,
          variantIdCorrected: true,
          packingCustomerIdCorrected: isTemplate2 ? (valueConfidence === 100) : true,
        };
        newErrors.splice(indexToAdd, 0, newError);
        newErrors = newErrors.map((error, idx) => {
          // Don't increment index for metadata (type string)
          if (idx > indexToAdd && typeof error.index === "number") {
            return {
              ...error,
              index: error.index + 1,
            };
          }
          return error;
        });
        console.log(`adjustedErrors`, newErrors);
        return newErrors;
      };
      return adjustedErrors();
    });
    setIsErrorsInit(true);
  };

  const errorCountDown = useMemo(() => {
    if (isErrorsInit && Array.isArray(errors)) {
      let count = 0;

      errors.forEach((error) => {
        if (typeof error.index === "number") {
          if (
            !error.productIdCorrected ||
            !error.quantityIdCorrected ||
            !error.variantIdCorrected ||
            !error.packingCustomerIdCorrected
          ) {
            count += 1;
          }
        } else {
          const correctedKey = `${error.index}Corrected`;
          if (error[correctedKey] === false) {
            count += 1;
          }
        }
      });

      return count;
    }
    return 0;
  }, [errors, isErrorsInit]);

  const hasMetadataErrors = useMemo(() => {
    if (!isErrorsInit || !Array.isArray(errors)) return false;

    return errors.some((error) => {
      if (typeof error.index === "string") {
        const correctedKey = `${error.index}Corrected`;
        return error[correctedKey] === false;
      }
      return false;
    });
  }, [errors, isErrorsInit]);

  const hasProductErrors = useMemo(() => {
    if (!isErrorsInit || !Array.isArray(errors)) return false;

    return errors.some((error) => {
      if (typeof error.index === "number") {
        return (
          !error.productIdCorrected ||
          !error.quantityIdCorrected ||
          !error.variantIdCorrected ||
          !error.packingCustomerIdCorrected
        );
      }
      return false;
    });
  }, [errors, isErrorsInit]);

  // Managing modified indexes
  const [selectedIndices, setSelectedIndices] = useState(new Set());

  // Unsaved changes warning
  const hasUnsavedChanges = isDirty || selectedIndices.size > 0 || fieldChanged || hasFormBeenTouched;
  const { isBlocked, proceed, reset } = useUnsavedChangesWarning(
    hasUnsavedChanges,
    t("unsaved_changes_message")
  );

  const handleSelectedIndicesChange = (newIndex) => {
    setSelectedIndices((prevIndices) => {
      const newIndices = new Set(prevIndices);
      newIndices.add(newIndex);
      return newIndices;
    });
  };

  const updateIndices = (indexToUpdate, action) => {
    setSelectedIndices((prevIndices) => {
      const updatedIndices = new Set();
      prevIndices.forEach((currentIndex) => {
        if (action === "add") {
          if (currentIndex >= indexToUpdate) {
            updatedIndices.add(currentIndex + 1);
          } else {
            updatedIndices.add(currentIndex);
          }
        } else if (action === "delete") {
          if (currentIndex < indexToUpdate) {
            updatedIndices.add(currentIndex);
          } else if (currentIndex > indexToUpdate) {
            updatedIndices.add(currentIndex - 1);
          }
        }
      });
      return updatedIndices;
    });
  };

  const handleInternalNote = () => {
    setInternalNoteInit(true);
  };

  const handleOpenAddProduct = (value) => {
    if (value) {
      setSelected("product");
    }
    setOpenAddProduct(value);
  };

  useEffect(() => {
    if (customerIDSelect) {
      setIsModalOpen(true);
    }
  }, [customerIDSelect]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const {
      customerInstructions,
      groupInstructions,
      hasInstructions
    } = useCustomerInstructions(customerIDSelect, customerGroupIdSelect);

  usePastPurchases(customerIDSelect);

  const isInfosCustomersEnabled = settingsInterface?.settings?.display?.features?.infos_customers === true;
  const organizationName = settingsInterface?.settings?.organization?.name || 'Default';
  const InfosCustomersTemplate = useTemplateLoader(
    'InfosCustomers',
    organizationName,
    InfosCustomersDefault,
    isInfosCustomersEnabled
  );

  if (!dataForm && !isInitialized) {
    return (
      <div className="flex justify-center items-center w-full h-[100vh]">
        <BoxesLoader />
      </div>
    );
  }

  return (
    <>
      <SelectProvider>
        {!isDataReadOnlyOperators && (
          <ActionsToolbar
            status={dataForm?.process?.status}
            orderId={params.orderId}
            internalNoteInit={internalNoteInit}
            onInternalNote={handleInternalNote}
            onModalAddProduct={handleOpenAddProduct}
          />
        )}
        <div className="flex">
          <div className={`w-6/12 mr-8 bg-white rounded-lg relative ${isScrollSynced ? '' : 'sticky top-22'}`}>
            <PaginationComponent
              page={page}
              pages={pages}
              onPageChange={handlePageChange}
              currentErrors={errors}
            />

            <BasicExampleWithImage
              orderImage={dataForm?.process?.email.images[page - 1]}
              orderPdf={dataForm?.process?.email?.images_pdf[page - 1]}
              hoveredShapeId={hoveredShapeId}
              onHoveredShapeDraw={handleHoveredShapeDraw}
              shapes={currentShapes}
              urlPdf={dataForm?.process?.email?.input_presigned}
              orderId={orderId}
              onScrollSyncChange={setIsScrollSynced}
            />
          </div>

          <div className="w-6/12 mr-3">
            <div className="flex w-full flex-col">
              <FormProvider {...methods}>
                <form
                  onSubmit={methods.handleSubmit(onSubmit)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                  className="flex flex-col items-start"
                >
                  <StatusWarning
                    errorCount={errorCountDown}
                    status={dataForm?.process?.status}
                    loading={isSending}
                    initialDataLoading={initialDataLoading}
                    orderId={orderId}
                    parsing={dataForm?.process?.parsing?.products?.FLAG_MANUAL_PARSING_DONE}
                    errors={errors}
                  />

                  {internalNoteInit && <InternalNote methods={methods} orderId={orderId} status={dataForm?.process?.status} />}

                  <Intents data={dataForm?.order?.metadata?.intents} />

                  {hasInstructions && (
                    <Accordion
                      variant="splitted"
                      itemClasses={itemClasses}
                      className="px-0"
                      fullWidth
                    >
                      <AccordionItem
                        key="1"
                        aria-label="instructions"
                        title="Instructions spécifiques client"
                      >
                        {customerInstructions?.customer_instruction && (
                          <p className="whitespace-pre-line">
                            {customerInstructions.customer_instruction}
                          </p>
                        )}
                        {customerInstructions?.customer_instruction &&
                          groupInstructions?.customer_instruction && <hr className="my-2" />}
                        {groupInstructions?.customer_instruction && (
                          <p className="whitespace-pre-line">
                            {groupInstructions.customer_instruction}
                          </p>
                        )}
                      </AccordionItem>
                    </Accordion>
                  )}

                  <div className="relative w-full">
                    <Tabs
                      aria-label={t("options")}
                      disabledKeys={isCustomerSelected || isDebugPanelOpen ? [] : ["product"]}
                      selectedKey={selected}
                      onSelectionChange={setSelected}
                      variant="underlined"
                      classNames={classNames}
                    >
                      <Tab key="mail" data-cy="order__tab_mail" title={t("original_mail")}>
                        <div className="p-5 text-sm">
                          {dataForm?.process ? (
                            <EmailContentAdvanced orderId={orderId} />
                          ) : (
                            <p>{t("no_content")}</p>
                          )}
                        </div>
                      </Tab>
                      <Tab
                        key="infos"
                        data-cy="order__tab_infos"
                        title={
                          <div className="flex items-center space-x-2">
                            {hasMetadataErrors && (
                              <IoWarningOutline className="fill-orange-500 text-orange-500 text-lg" />
                            )}
                            <span>{t("customer_information")}</span>
                          </div>
                        }
                      >
                        <CustomerInfoTab
                          dataForm={dataForm}
                          methods={methods}
                          setErrors={setErrors}
                          classNames={classNames}
                          selectedCustomer={customerIDSelect}
                          currentErrors={errors}
                        />

                        {settingsInterface?.settings?.display?.features?.infos_customers === true && (
                          <InfosCustomersTemplate customerIDSelect={customerIDSelect} />
                        )}
                       
                      </Tab>

                      <Tab
                        key="product"
                        data-cy="order__tab_product"
                        title={
                          <div className="flex items-center space-x-2">
                            {hasProductErrors && (
                              <IoWarningOutline className="fill-orange-500 text-orange-500 text-lg" />
                            )}
                            <span>{t("products")}</span>
                          </div>
                        }
                      >
                        <Products
                          control={methods.control}
                          setValue={methods.setValue}
                          data={dataForm}
                          currentPage={page}
                          selectedCustomer={customerIDSelect}
                          onProductDelete={handleProductDelete}
                          onAddProduct={handleAddProduct}
                          onProductUpdate={handleProductUpdate}
                          shouldReloadData={shouldReloadData}
                          setShouldReloadData={setShouldReloadData}
                          onHoveredShapeId={handleHoveredShapeId}
                          hoveredShapeDraw={hoveredShapeDraw}
                          currentErrors={errors}
                          trackingError={handleTrackingError}
                          selectedIndices={selectedIndices}
                          onSelectedIndicesChange={handleSelectedIndicesChange}
                          openAddProduct={openAddProduct}
                          onModalAddProduct={handleOpenAddProduct}
                        />
                      </Tab>
                      <Tab key="logs" data-cy="order__tab_logs" title={t("action_history")}>
                        <UserActionsFeed dataForm={dataForm} />
                      </Tab>
                      {dataForm?.process?.status === "success" &&
                       settingsInterface?.settings?.display?.features?.third_party_ref === true && (
                        <Tab
                          key="tpreference"
                          data-cy="order__tab_tpref"
                          title={t("third_party_reference")}
                        >
                          <ThirdPartyReference
                            data={dataForm}
                            onHoveredShapeId={handleHoveredShapeId}
                            hoveredShapeDraw={hoveredShapeDraw}
                            currentPage={page}
                            control={methods.control}
                            setValue={methods.setValue}
                            getValues={methods.getValues}
                          />
                        </Tab>
                      )}
                    </Tabs>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </SelectProvider>
      <CustomerInstructionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        customerId={customerIDSelect}
        customerGroupId={customerGroupIdSelect}
      />
      {isAdminVistaflow && <DebugPanel orderId={orderId} dataForm={dataForm} onOpenChange={setIsDebugPanelOpen} />}
      <UnsavedChangesModal
        isOpen={isBlocked}
        onStay={reset}
        onLeave={proceed}
      />
    </>
  );
}
